import { useState, useEffect, useCallback, useMemo } from "react";
import type { FormEvent } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { neon } from "@/integrations/neon";
import { Loader2, ExternalLink, Film, Star, CheckCircle2, Lock, FileText } from "lucide-react";
import { sanitizeInput, validateEmail, validateLength, logSecurityEvent } from "@/utils/security";
import { getErrorMessage } from "@/utils/errors";
import { LanguageToggle } from "@/components/ui/LanguageToggle";
import { useTranslation } from "@/i18n/useTranslation";
import { cn } from "@/lib/utils";

interface ReviewScriptInfo {
  movie_name: string;
  custom_message: string | null;
  script_url: string | null;
  script_url_th: string | null;
  password_protected: boolean;
  review_password: string | null;
  review_password_hash: string | null;
  version_number: number;
  version_name: string | null;
  version_notes: string | null;
  version_notes_th: string | null;
}

interface ReviewFormQuestion {
  id: string;
  question_name: string;
  question_name_th: string | null;
  question_description: string | null;
  question_description_th: string | null;
  poor_description: string | null;
  poor_description_th: string | null;
  excellent_description: string | null;
  excellent_description_th: string | null;
  weight_percentage: number;
}

interface ReviewFormData {
  name: string;
  name_th: string | null;
  description: string | null;
  description_th: string | null;
  form_questions: ReviewFormQuestion[];
}

interface ReviewRpcRow {
  script_form_id: string;
  movie_name: string;
  custom_message: string | null;
  script_url: string | null;
  script_url_th: string | null;
  password_protected: boolean;
  review_password: string | null;
  review_password_hash: string | null;
  form_name: string;
  form_name_th: string | null;
  form_description: string | null;
  form_description_th: string | null;
  form_questions: ReviewFormQuestion[] | null;
  version_number: number;
  version_name: string | null;
  version_notes: string | null;
  version_notes_th: string | null;
}

type AnswersState = Record<string, number>;

type TranslateFn = ReturnType<typeof useTranslation>["t"];
type LanguageCode = ReturnType<typeof useTranslation>["language"];

const RATING_VALUES = [1, 2, 3, 4, 5] as const;

interface ReviewHeaderProps {
  scriptData: ReviewScriptInfo | null;
  language: LanguageCode;
  t: TranslateFn;
  onScriptClick: (url: string) => void;
}

const ReviewHeader = ({ scriptData, language, t, onScriptClick }: ReviewHeaderProps) => {
  if (!scriptData) {
    return null;
  }

  const showVersionDetails = Boolean(
    scriptData.version_number || scriptData.version_notes || scriptData.version_notes_th
  );

  return (
    <div className="text-center mb-8 space-y-3">
      <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-primary/10 text-primary border border-primary/20 shadow-2xs mb-1">
        <Film className="h-8 w-8" />
      </div>
      
      <div className="space-y-1">
        <h1 className="text-3xl font-extrabold text-foreground tracking-tight sm:text-4xl">
          {scriptData.movie_name}
        </h1>
        {scriptData.version_number && (
          <div className="flex items-center justify-center gap-2 pt-1">
            <Badge variant="secondary" className="text-xs font-semibold px-2.5 py-0.5">
              v{scriptData.version_number}
              {scriptData.version_name && ` · ${scriptData.version_name}`}
            </Badge>
            {scriptData.password_protected && (
              <Badge variant="outline" className="text-xs gap-1 text-muted-foreground">
                <Lock className="h-3 w-3" />
                {t("dashboard.participants.badge_protected")}
              </Badge>
            )}
          </div>
        )}
      </div>

      {scriptData.custom_message && (
        <p className="text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">
          {scriptData.custom_message}
        </p>
      )}

      {showVersionDetails && (
        <div className="max-w-xl mx-auto">
          <p className="text-xs text-muted-foreground/90 italic bg-muted/30 py-1.5 px-3 rounded-lg border border-border/50">
            {language === "th" && scriptData.version_notes_th
              ? scriptData.version_notes_th
              : scriptData.version_notes}
          </p>
        </div>
      )}

      {/* Script Reader Buttons */}
      <div className="pt-2 flex flex-wrap justify-center gap-3">
        {scriptData.script_url && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onScriptClick(scriptData.script_url!)}
            className="rounded-xl shadow-2xs gap-2 border-border/80 hover:border-primary text-foreground hover:text-primary font-medium"
          >
            <FileText className="h-4 w-4" />
            {t("common.view_script")} {scriptData.script_url_th ? "(English)" : ""}
            <ExternalLink className="h-3.5 w-3.5 opacity-60 ml-0.5" />
          </Button>
        )}
        {scriptData.script_url_th && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onScriptClick(scriptData.script_url_th!)}
            className="rounded-xl shadow-2xs gap-2 border-border/80 hover:border-primary text-foreground hover:text-primary font-medium"
          >
            <FileText className="h-4 w-4" />
            {t("common.view_script")} (Thai)
            <ExternalLink className="h-3.5 w-3.5 opacity-60 ml-0.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

interface ReviewQuestionProps {
  question: ReviewFormQuestion;
  index: number;
  language: LanguageCode;
  currentScore?: number;
  onScoreChange: (questionId: string, score: number) => void;
  t: TranslateFn;
  hasError?: boolean;
}

const ReviewQuestion = ({
  question,
  index,
  language,
  currentScore,
  onScoreChange,
  t,
  hasError,
}: ReviewQuestionProps) => {
  const questionTitle = language === "th" && question.question_name_th ? question.question_name_th : question.question_name;
  const questionDescription = language === "th" && question.question_description_th ? question.question_description_th : question.question_description;
  const poorDescription = language === "th" && question.poor_description_th ? question.poor_description_th : question.poor_description;
  const excellentDescription = language === "th" && question.excellent_description_th ? question.excellent_description_th : question.excellent_description;

  return (
    <div
      id={`question-${question.id}`}
      className={cn(
        "rounded-2xl border p-4 sm:p-5 transition-all duration-200 bg-card/60 space-y-4 shadow-2xs",
        hasError ? "border-destructive/80 bg-destructive/5 ring-1 ring-destructive/40" : "border-border/80 hover:border-border"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
              {index + 1}
            </span>
            <Label className="text-base font-bold text-foreground leading-snug cursor-pointer">
              {questionTitle}
            </Label>
          </div>
          {questionDescription && (
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed pl-8">
              {questionDescription}
            </p>
          )}
        </div>
        {question.weight_percentage > 0 && (
          <Badge variant="outline" className="shrink-0 text-[11px] font-medium text-muted-foreground">
            {question.weight_percentage}%
          </Badge>
        )}
      </div>

      {/* 1-5 Segmented Rating Buttons */}
      <div className="space-y-2 pt-1">
        <div className="grid grid-cols-5 gap-2 sm:gap-3">
          {RATING_VALUES.map((val) => {
            const isSelected = currentScore === val;
            return (
              <button
                key={val}
                type="button"
                onClick={() => onScoreChange(question.id, val)}
                className={cn(
                  "flex h-12 sm:h-14 flex-col items-center justify-center rounded-xl border text-center font-bold transition-all duration-200",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  isSelected
                    ? "bg-primary text-primary-foreground border-primary shadow-md scale-[1.02] ring-2 ring-primary/20"
                    : "bg-background border-border/80 text-foreground hover:bg-muted/70 hover:border-border shadow-2xs hover:scale-[1.01]"
                )}
                aria-label={`Score ${val} out of 5 for ${questionTitle}`}
              >
                <span className="tabular-nums text-lg sm:text-xl">{val}</span>
              </button>
            );
          })}
        </div>

        {/* Rating Descriptors */}
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1 pt-1">
          <span className="font-medium max-w-[40%] text-left line-clamp-1">
            1 · {poorDescription || (language === "th" ? "ต้องปรับปรุง" : "Poor")}
          </span>
          <span className="text-center font-semibold text-foreground">
            {currentScore ? (
              <span className="text-primary tabular-nums">
                {t("review.score.label", { value: currentScore, max: 5 })}
              </span>
            ) : (
              <span className="opacity-50 text-[11px]">
                {language === "th" ? "เลือกคะแนน" : "Tap to rate"}
              </span>
            )}
          </span>
          <span className="font-medium max-w-[40%] text-right line-clamp-1">
            5 · {excellentDescription || (language === "th" ? "ยอดเยี่ยม" : "Excellent")}
          </span>
        </div>
      </div>
    </div>
  );
};

interface ReviewFormProps {
  formData: ReviewFormData | null;
  language: LanguageCode;
  answers: AnswersState;
  reviewerName: string;
  reviewerEmail: string;
  notes: string;
  isSubmitting: boolean;
  missingQuestions: Set<string>;
  onSubmit: (event: FormEvent) => void;
  onAnswerChange: (questionId: string, score: number) => void;
  onNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onNotesChange: (value: string) => void;
  t: TranslateFn;
}

const ReviewForm = ({
  formData,
  language,
  answers,
  reviewerName,
  reviewerEmail,
  notes,
  isSubmitting,
  missingQuestions,
  onSubmit,
  onAnswerChange,
  onNameChange,
  onEmailChange,
  onNotesChange,
  t,
}: ReviewFormProps) => {
  const displayFormName = language === "th" && formData?.name_th ? formData.name_th : formData?.name;
  const displayFormDescription = language === "th" && formData?.description_th ? formData.description_th : formData?.description;

  const totalQuestions = formData?.form_questions?.length ?? 0;
  const answeredCount = useMemo(() => {
    if (!formData?.form_questions) return 0;
    return formData.form_questions.filter((q) => typeof answers[q.id] === "number").length;
  }, [formData, answers]);

  const progressPercentage = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;
  const isComplete = totalQuestions > 0 && answeredCount === totalQuestions;

  return (
    <Card className="shadow-lg border-border/80 bg-card/95 backdrop-blur-md rounded-2xl overflow-hidden">
      <CardHeader className="space-y-2 pb-4 border-b border-border/60 bg-muted/20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-xl sm:text-2xl font-bold tracking-tight">{displayFormName}</CardTitle>
            {displayFormDescription && (
              <CardDescription className="text-sm text-muted-foreground mt-1">
                {displayFormDescription}
              </CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2 sm:self-start">
            {isComplete ? (
              <Badge className="bg-emerald-600 text-white gap-1 py-1 px-3 shadow-2xs font-semibold">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {language === "th" ? "ตอบครบทุกข้อแล้ว" : "All questions rated"}
              </Badge>
            ) : (
              <Badge variant="outline" className="tabular-nums font-semibold px-2.5 py-1 text-slate-700 dark:text-slate-300">
                {answeredCount} / {totalQuestions} {language === "th" ? "ข้อ" : "rated"}
              </Badge>
            )}
          </div>
        </div>

        {/* Form Progress Bar */}
        <div className="space-y-1.5 pt-2">
          <div className="flex justify-between text-xs text-muted-foreground font-medium">
            <span>{language === "th" ? "ความคืบหน้า" : "Evaluation Progress"}</span>
            <span className="tabular-nums font-semibold">{progressPercentage}%</span>
          </div>
          <Progress value={progressPercentage} className="h-2 rounded-full" />
        </div>
      </CardHeader>

      <CardContent className="p-5 sm:p-6">
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Reviewer Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl bg-muted/20 p-4 border border-border/60">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-sm font-semibold">
                {t("review.labels.name")}
              </Label>
              <Input
                id="name"
                value={reviewerName}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder={language === "th" ? "ชื่อของคุณ" : "Your full name"}
                required
                className="bg-background rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-semibold">
                {t("review.labels.email")}
              </Label>
              <Input
                id="email"
                type="email"
                value={reviewerEmail}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder={language === "th" ? "อีเมลของคุณ" : "your.email@example.com"}
                required
                className="bg-background rounded-xl"
              />
            </div>
          </div>

          {/* Assessment Questions */}
          <div className="space-y-4">
            {formData?.form_questions?.map((question, index) => (
              <ReviewQuestion
                key={question.id}
                question={question}
                index={index}
                language={language}
                currentScore={answers[question.id]}
                onScoreChange={onAnswerChange}
                t={t}
                hasError={missingQuestions.has(question.id)}
              />
            ))}
          </div>

          {/* Qualitative Notes */}
          <div className="space-y-2 pt-2">
            <Label htmlFor="notes" className="text-sm font-semibold">
              {t("review.labels.notes")}
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder={t("review.placeholders.notes")}
              rows={4}
              className="bg-background rounded-xl resize-y"
            />
          </div>

          {/* Submit Action */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base shadow-md hover:shadow-lg transition-all duration-200 rounded-xl"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {language === "th" ? "กำลังส่งแบบประเมิน..." : "Submitting Evaluation..."}
              </>
            ) : (
              <>
                <Star className="mr-2 h-5 w-5 fill-current" />
                {t("review.buttons.submit")}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

interface PasswordDialogProps {
  open: boolean;
  password: string;
  onPasswordChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  t: TranslateFn;
}

const ReviewPasswordDialog = ({ open, password, onPasswordChange, onClose, onSubmit, t }: PasswordDialogProps) => (
  <Dialog
    open={open}
    onOpenChange={(next) => {
      if (!next) {
        onClose();
      }
    }}
  >
    <DialogContent className="sm:max-w-md rounded-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-lg font-bold">
          <Lock className="h-5 w-5 text-primary" />
          {t("review.dialog.password_required")}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 py-2">
        <Label htmlFor="script-password" className="text-sm font-medium">
          {t("review.labels.password_prompt")}
        </Label>
        <Input
          id="script-password"
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder={t("review.placeholders.password")}
          onKeyDown={(e) => e.key === "Enter" && onSubmit()}
          className="rounded-xl"
          autoFocus
        />
      </div>
      <DialogFooter className="gap-2 sm:gap-0">
        <Button onClick={onClose} variant="outline" className="rounded-xl">
          {t("common.cancel")}
        </Button>
        <Button onClick={onSubmit} className="rounded-xl font-semibold">
          {t("review.buttons.access_script")}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const ReviewPage = () => {
  const { reviewLink } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scriptData, setScriptData] = useState<ReviewScriptInfo | null>(null);
  const [formData, setFormData] = useState<ReviewFormData | null>(null);
  const [scriptFormId, setScriptFormId] = useState<string | null>(null);
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [answers, setAnswers] = useState<AnswersState>({});
  const [missingQuestions, setMissingQuestions] = useState<Set<string>>(new Set());
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const { toast } = useToast();
  const { t, language } = useTranslation();

  const fetchReviewData = useCallback(async () => {
    if (!reviewLink) {
      setIsLoading(false);
      toast({
        title: t("review.errors.invalid_link_title"),
        description: t("review.errors.invalid_link_description"),
        variant: "destructive",
      });
      return;
    }

    try {
      const rpcData = (await neon.api.reviews.getReviewData(reviewLink)) as ReviewRpcRow[];

      if (!rpcData || rpcData.length === 0) {
        throw new Error("No review data found for this link");
      }

      const reviewData = rpcData[0];
      setScriptFormId(reviewData.script_form_id);

      // Set script data
      setScriptData({
        movie_name: reviewData.movie_name,
        custom_message: reviewData.custom_message,
        script_url: reviewData.script_url,
        script_url_th: reviewData.script_url_th,
        password_protected: reviewData.password_protected,
        review_password: reviewData.review_password,
        review_password_hash: reviewData.review_password_hash,
        version_number: reviewData.version_number,
        version_name: reviewData.version_name,
        version_notes: reviewData.version_notes,
        version_notes_th: reviewData.version_notes_th,
      });

      // Set form data
      const rawQuestions = Array.isArray(reviewData.form_questions)
        ? (reviewData.form_questions as unknown[])
        : [];

      const normalisedQuestions: ReviewFormQuestion[] = rawQuestions.map((question) => {
        const entry = question as Record<string, unknown>;
        const id = typeof entry.id === "string" ? entry.id : String(entry.id ?? "");
        return {
          id,
          question_name: typeof entry.question_name === "string" ? entry.question_name : "",
          question_name_th: typeof entry.question_name_th === "string" ? entry.question_name_th : null,
          question_description: typeof entry.question_description === "string" ? entry.question_description : null,
          question_description_th: typeof entry.question_description_th === "string" ? entry.question_description_th : null,
          poor_description: typeof entry.poor_description === "string" ? entry.poor_description : null,
          poor_description_th: typeof entry.poor_description_th === "string" ? entry.poor_description_th : null,
          excellent_description: typeof entry.excellent_description === "string" ? entry.excellent_description : null,
          excellent_description_th: typeof entry.excellent_description_th === "string" ? entry.excellent_description_th : null,
          weight_percentage:
            typeof entry.weight_percentage === "number"
              ? entry.weight_percentage
              : Number(entry.weight_percentage ?? 0),
        };
      });

      setFormData({
        name: reviewData.form_name,
        name_th: reviewData.form_name_th,
        description: reviewData.form_description,
        description_th: reviewData.form_description_th,
        form_questions: normalisedQuestions,
      });

      // Intentionally do NOT pre-populate with default 3 score to eliminate survey bias
      setAnswers({});
      setIsLoading(false);
    } catch (error: unknown) {
      console.error("Review fetch error:", error);
      setIsLoading(false);
      toast({
        title: t("review.errors.not_found_title"),
        description: getErrorMessage(error, t("review.errors.not_found_description")),
        variant: "destructive",
      });
    }
  }, [reviewLink, toast, t]);

  useEffect(() => {
    fetchReviewData();
  }, [fetchReviewData]);

  const closePasswordDialog = () => {
    setShowPasswordDialog(false);
    setPassword("");
  };

  const handleScriptUrlClick = (url: string) => {
    if (scriptData?.password_protected) {
      setPendingUrl(url);
      setShowPasswordDialog(true);
    } else {
      window.open(url, "_blank");
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      const result = await neon.api.reviews.verifyPassword(
        password,
        scriptData?.review_password_hash || scriptData?.review_password
      );

      if (result.isValid) {
        closePasswordDialog();
        window.open(pendingUrl!, "_blank");
        setPendingUrl(null);

        // Log security event
        await logSecurityEvent("script_access_granted", { script_id: scriptFormId });
      } else {
        await logSecurityEvent("script_access_denied", { script_id: scriptFormId, reason: "invalid_password" });
        toast({
          title: t("review.errors.incorrect_password_title"),
          description: t("review.errors.incorrect_password_description"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Password verification error:", error);
      toast({
        title: t("review.errors.password_error_title"),
        description: t("review.errors.password_error_description"),
        variant: "destructive",
      });
    }
  };

  const handleAnswerChange = (questionId: string, score: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: score }));
    setMissingQuestions((prev) => {
      if (prev.has(questionId)) {
        const next = new Set(prev);
        next.delete(questionId);
        return next;
      }
      return prev;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validate that all questions have been answered
    const questions = formData?.form_questions ?? [];
    const missing = new Set<string>();
    questions.forEach((q) => {
      if (typeof answers[q.id] !== "number") {
        missing.add(q.id);
      }
    });

    if (missing.size > 0) {
      setMissingQuestions(missing);
      const firstMissingId = Array.from(missing)[0];
      const element = document.getElementById(`question-${firstMissingId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      toast({
        title: language === "th" ? "กรุณาตอบคำถามให้ครบทุกข้อ" : "Please answer all questions",
        description:
          language === "th"
            ? `ยังเหลืออีก ${missing.size} ข้อที่ยังไม่ได้ให้คะแนน`
            : `There are ${missing.size} unanswered question(s).`,
        variant: "destructive",
      });
      return;
    }

    // Input validation and sanitization
    const sanitizedName = sanitizeInput(reviewerName);
    const sanitizedEmail = sanitizeInput(reviewerEmail);
    const sanitizedNotes = sanitizeInput(notes);

    if (!validateLength(sanitizedName, 1, 255)) {
      toast({
        title: t("review.errors.invalid_input_title"),
        description: t("review.errors.invalid_name_description"),
        variant: "destructive",
      });
      return;
    }

    if (!validateEmail(sanitizedEmail)) {
      toast({
        title: t("review.errors.invalid_email_title"),
        description: t("review.errors.invalid_email_description"),
        variant: "destructive",
      });
      return;
    }

    if (sanitizedNotes && !validateLength(sanitizedNotes, 0, 5000)) {
      toast({
        title: t("review.errors.invalid_input_title"),
        description: t("review.errors.invalid_notes_description"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await logSecurityEvent("feedback_submission_attempt", {
        script_form_id: scriptFormId,
        reviewer_email: sanitizedEmail,
      });

      const answersArray = Object.entries(answers).map(([questionId, score]) => ({
        question_id: questionId,
        score,
      }));

      const data = await neon.api.reviews.submitFeedback({
        script_form_id: scriptFormId!,
        reviewer_name: sanitizedName,
        reviewer_email: sanitizedEmail,
        notes: sanitizedNotes || null,
        answers: answersArray,
      });

      await logSecurityEvent("feedback_submission_success", {
        script_form_id: scriptFormId,
        submission_id: data.submissionId,
      });

      toast({
        title: t("review.success.submission_title"),
        description: t("review.success.submission_description"),
      });
    } catch (error: unknown) {
      toast({
        title: t("review.errors.submission_error_title"),
        description: getErrorMessage(error, t("review.errors.submission_error_description")),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6 flex justify-end">
          <LanguageToggle />
        </div>
        <ReviewHeader
          scriptData={scriptData}
          language={language}
          t={t}
          onScriptClick={handleScriptUrlClick}
        />

        <ReviewForm
          formData={formData}
          language={language}
          answers={answers}
          reviewerName={reviewerName}
          reviewerEmail={reviewerEmail}
          notes={notes}
          isSubmitting={isSubmitting}
          missingQuestions={missingQuestions}
          onSubmit={handleSubmit}
          onAnswerChange={handleAnswerChange}
          onNameChange={setReviewerName}
          onEmailChange={setReviewerEmail}
          onNotesChange={setNotes}
          t={t}
        />

        <ReviewPasswordDialog
          open={showPasswordDialog}
          password={password}
          onPasswordChange={setPassword}
          onClose={closePasswordDialog}
          onSubmit={handlePasswordSubmit}
          t={t}
        />
      </div>
    </div>
  );
};

export default ReviewPage;
